'use client';

import {
  useDynamicContext,
  useDynamicModals,
  useUserWallets,
} from '@dynamic-labs/sdk-react-core';
import { Listbox, ListboxButton, ListboxOptions } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import Image from 'next/image';
import { useEffect } from 'react';

import { Metadata } from '@repo/types-client';
import { tw } from '@repo/ui';
import { getImageUrl, shortenEthAddress } from '@repo/utils';

import { WalletOption } from './wallet-option';

interface SolanaWalletDropdownProps {
  selectedAddress: string | null;
  setSelectedAddress: (address: string | null) => void;
}

export const SolanaWalletDropdown: React.FC<SolanaWalletDropdownProps> = ({
  selectedAddress,
  setSelectedAddress,
}) => {
  const userWallets = useUserWallets();
  const { user } = useDynamicContext();
  const { setShowLinkNewWalletModal } = useDynamicModals();

  const solWallets = userWallets.filter((wallet) => wallet.chain === 'SOL');

  useEffect(() => {
    if (userWallets && solWallets.length) {
      if (
        (user?.metadata as Metadata)?.solanaClaimAddress &&
        solWallets.some(
          (wallet) =>
            wallet.address === (user?.metadata as Metadata)?.solanaClaimAddress,
        )
      ) {
        setSelectedAddress(
          (user?.metadata as Metadata)?.solanaClaimAddress ?? null,
        );
      } else {
        setSelectedAddress(solWallets[0]?.address ?? null);
      }
    }
    // this is intended to run only on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Listbox
      as={'div'}
      value={selectedAddress}
      onChange={setSelectedAddress}
      className={'flex items-center gap-3'}
    >
      <div className='relative w-full'>
        <ListboxButton
          className={tw(
            'grid w-full cursor-default grid-cols-1 rounded-xl bg-white bg-opacity-[.08]',
            'py-2 pl-3 pr-2 text-left text-sm font-semibold text-white md:text-base',
          )}
        >
          <span className='col-start-1 row-start-1 flex items-center gap-2 pr-6'>
            <div className='relative h-[30px] w-[30px] rounded-full'>
              <Image
                fill
                src={getImageUrl('/claim/images/solana.png')}
                alt='Solana logo'
                sizes='(max-width: 640px) 30px, 40px'
              />
            </div>
            <span className='block truncate tracking-[1.6px]'>
              {selectedAddress
                ? shortenEthAddress(selectedAddress, 6, 6)
                : null}
            </span>
          </span>
          <ChevronDownIcon
            aria-hidden='true'
            className={tw(
              'col-start-1 row-start-1 size-6 self-center',
              'justify-self-end text-white hover:cursor-pointer',
            )}
          />
        </ListboxButton>

        <ListboxOptions
          transition
          className={tw(
            'absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-[#363636]',
            'py-1 shadow-lg focus:outline-none',
            'data-[closed]:data-[leave]:opacity-0 data-[leave]:transition',
            'data-[leave]:duration-100 data-[leave]:ease-in',
          )}
        >
          {solWallets.map((wallet) => (
            <WalletOption
              key={wallet.id}
              wallet={wallet}
            />
          ))}
          <div
            onClick={() => setShowLinkNewWalletModal(true)}
            className={tw(
              'group relative cursor-default select-none py-2 pl-3 pr-9 text-white',
              'hover:cursor-pointer hover:text-white/50 data-[focus]:outline-none',
              'text-sm md:text-base',
            )}
          >
            <div className='flex items-center gap-2 font-semibold tracking-[1.6px]'>
              + LINK ANOTHER SOLANA WALLET
            </div>
          </div>
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
